import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Progress as CardsCountTaskProgressBar } from '@sorare/core/src/atoms/loader/Progress';
import { BodyS, LabelL } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionTaskFooter } from '../CollectionTaskFooter';
import { DumbTask } from '../DumbTask';
import { Colors } from '../getTaskColors';
import { CardCollectionDetailsDialog } from './CardCollectionDetailsDialog';
import type { CardCollectionTask_taskInterface } from './__generated__/index.graphql';

const Img = styled.img`
  width: var(--triple-unit);
  @container ${laptopAndAbove} {
    width: var(--quadruple-unit);
  }
`;
const Footer = styled.div`
  flex: 1;
`;

type Props = {
  cardCollectionTask: CardCollectionTask_taskInterface;
  featured?: boolean;
};
export const CardCollectionTask = ({ cardCollectionTask, featured }: Props) => {
  if (!isType(cardCollectionTask, 'CardsCountTask')) {
    return null;
  }

  const {
    templateIconUrl,
    iconUrl,
    title,
    description,
    liveScore,
    thresholds,
    color,
  } = cardCollectionTask;

  const currentThresholdIndex = thresholds.findIndex(
    ({ current }) => !!current
  );

  return (
    <DumbTask
      featured={featured}
      color={liveScore > 0 ? Colors.YELLOW : undefined}
      borderColor={liveScore > 0 ? color : undefined}
      icon={!!templateIconUrl && <Img src={templateIconUrl} alt="" />}
      title={
        <Horizontal>
          <LabelL bold>{title}</LabelL>
          {!!iconUrl && <img width={20} src={iconUrl} alt="" />}
        </Horizontal>
      }
      description={
        liveScore > 0 && (
          <BodyS>
            <Ellipsis visibleLines={2}>{description}</Ellipsis>
          </BodyS>
        )
      }
      detailsDialog={({ open, onClose }) => (
        <CardCollectionDetailsDialog
          open={open}
          onClose={onClose}
          task={cardCollectionTask}
        />
      )}
      footer={
        <Footer>
          <CollectionTaskFooter
            collectionTask={cardCollectionTask}
            targetIcon={<ScarcityIcon rarity={Rarity.common} />}
          >
            <CardsCountTaskProgressBar
              value={liveScore}
              min={thresholds[currentThresholdIndex - 1]?.count}
              max={thresholds[currentThresholdIndex]?.count}
              color="linear-gradient(to right, var(--c-yellow-500), var(--c-yellow-300))"
              bgColor="var(--c-nd-150-opaque)"
            />
          </CollectionTaskFooter>
        </Footer>
      }
    />
  );
};

CardCollectionTask.fragments = {
  taskInterface: gql`
    fragment CardCollectionTask_taskInterface on TaskInterface {
      id
      iconUrl
      templateIconUrl
      title
      description
      color
      ... on CardsCountTask {
        id
        liveScore
        thresholds {
          count
          current
        }
      }
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        thresholds {
          count
          current
        }
      }
      ...CollectionTaskFooter_taskInterface
      ...CardCollectionDetailsDialog_taskInterface
    }
    ${CollectionTaskFooter.fragments.taskInterface}
    ${CardCollectionDetailsDialog.fragments.taskInterface}
  ` as TypedDocumentNode<CardCollectionTask_taskInterface>,
};
